'use client';

import { useState, useEffect } from 'react';
import { useWebPush, requestWebPushPermission } from '@lib/hooks/useWebPush';

/**
 * WebPushRegistrar
 *
 * Mount this once in the root layout. It does two things:
 * 1. Silently registers the service worker and re-activates any existing push subscription.
 * 2. If the user hasn't been asked yet, shows a small opt-in banner after a short delay.
 *
 * The banner is intentionally unobtrusive — shown only once per browser, dismissed forever
 * by clicking Allow or No thanks (stored in localStorage).
 */
export function WebPushRegistrar() {
  useWebPush(); // Always register SW + resume existing subscription

  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Only show banner if browser supports push AND user hasn't decided yet
    if (
      !('Notification' in window) ||
      !('serviceWorker' in navigator) ||
      !('PushManager' in window) ||
      Notification.permission !== 'default' ||
      localStorage.getItem('web-push-dismissed') === '1'
    ) {
      return;
    }

    // Delay banner by 5 seconds — don't interrupt page load
    const timer = setTimeout(() => setShowBanner(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleAllow = async () => {
    setShowBanner(false);
    const result = await requestWebPushPermission();
    if (result === 'denied') {
      localStorage.setItem('web-push-dismissed', '1');
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('web-push-dismissed', '1');
  };

  if (!showBanner) return null;

  return (
    <div
      role="dialog"
      aria-label="Enable push notifications"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm mx-auto"
    >
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg px-5 py-4 flex items-start gap-3">
        {/* Bell icon */}
        <span className="text-xl mt-0.5" aria-hidden>🔔</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            Stay in the loop
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Get notified about deals, restocks and order updates.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={handleAllow}
              className="px-3 py-1.5 text-xs font-medium bg-black dark:bg-white text-white dark:text-black rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
            >
              Allow
            </button>
            <button
              onClick={handleDismiss}
              className="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              No thanks
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          aria-label="Close"
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 mt-0.5 shrink-0"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
